/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  ChatAuthContext,
  FinishNodeState,
  RenderNodeState,
  SanitizedSerializedHistory,
  Schema,
  SchemaData,
  Script,
  SerializedHistory,
  SerializedSessionWithHistory,
  Session,
  Store,
} from '@lemonade-hq/chat-engine';
import {
  createNewSessionState,
  extractBackgroundDataFromPossiblyTruncatedHistory,
  generateStore,
  generateStoreForNewSession,
  getAllAnswersFromHistory,
  getRelevantSerializedSchema,
  getTruncatedHistory,
  isFinishNodeState,
  isRenderNodeState,
  serializeNodeState,
  SessionAnsweringStrategy,
  SessionStatus,
  updateSessionStateWithAnswers,
} from '@lemonade-hq/chat-engine';
import { useWizardNavigationContext } from '@lemonade-hq/chat-wizard-client-base';
import deepmerge from 'deepmerge';
import { produce } from 'immer';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatEngineSession } from './chat_engine.client';
import { usePersistenceHelpers } from './usePersistenceHelpers';
import type { WizardPersistenceContextSubmissionOptions } from './wizard_persistence.context';
import { WizardPersistenceContext } from './wizard_persistence.context';

// TODO: support perform nodes
// TODO: support subscript/island nodes
// TODO: support login nodes
// TODO: support resync

// TYPES

export interface WizardMemoryPersistenceProps {
  readonly script: Script & { readonly uuid: string };
  readonly answers?: SchemaData;
  readonly backgroundData?: SchemaData;
}

// HELPERS

const dummyI18nProvider = {
  t: (key: string) => key,
  translate: (key: string) => key,
};

const baseDummySession = {
  sessionId: 'dummy-session',
  assets: [],
  script: {
    id: 'dummy-script',
    name: 'dummy-script-name',
    engineVersion: '0.0.0',
    version: '0.0.0',
  },
};

const baseMockStore = {
  i18n: dummyI18nProvider,
  sessionMetadata: {} as Store['sessionMetadata'],
  requestMetadata: {} as Store['requestMetadata'],
  auth: {} as ChatAuthContext,
  config: {
    get: () => '',
    getBoolean: () => false,
    getInt: () => 0,
    getObject: () => ({}),
  } as Store['config'],
};

function serializeHistoryAndSchema(
  script: Script,
  session: Session,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { readonly history: SerializedHistory<any, unknown>; readonly schema: Schema } {
  const returnedNodes = session.history.filter(
    n => isRenderNodeState(n) || isFinishNodeState(n),
  ) as unknown as SanitizedSerializedHistory;
  const relevantAnswerKeys = returnedNodes
    .filter(isRenderNodeState)
    .flatMap(n => n.renderedStep.data?.attributes?.map(attr => attr.split('.')[0]) ?? []);
  const schema = getRelevantSerializedSchema(script.answersSchema, relevantAnswerKeys, dummyI18nProvider);
  const history = returnedNodes.map(returnedNode =>
    serializeNodeState(returnedNode as FinishNodeState | RenderNodeState, dummyI18nProvider),
  ) as SerializedHistory<any, unknown>; // eslint-disable-line @typescript-eslint/no-explicit-any

  return { schema, history };
}

async function getInitialFakeSession(
  script: Script,
  answers: SchemaData = {},
  backgroundData: SchemaData = {},
): Promise<ChatEngineSession> {
  const store = generateStoreForNewSession({
    script,
    answers,
    backgroundData,
    ...baseMockStore,
  });
  const session = await createNewSessionState(script, store);

  return {
    ...serializeHistoryAndSchema(script, session),
    sessionStatus: SessionStatus.Pending,
    auth: {
      visitorId: 'dummy-user',
    },
    ...baseDummySession,
  };
}

async function progressFakeSession(
  script: Script,
  session: Session,
  newAnswers: SchemaData,
  nodeHistoryId: string,
  answeringStrategy?: SessionAnsweringStrategy,
): Promise<SerializedSessionWithHistory> {
  // TODO: getting answers/bg data for store is duplicated from `sessions_answers.service.ts` - might want to unify them
  const inResyncOrRevise =
    answeringStrategy === SessionAnsweringStrategy.Resync || answeringStrategy === SessionAnsweringStrategy.Revise;
  const pastAnswers = getAllAnswersFromHistory(
    getTruncatedHistory(session.history, nodeHistoryId, { includeLastNode: inResyncOrRevise }).history,
  );
  const answers = deepmerge(pastAnswers, inResyncOrRevise ? newAnswers : {});
  const backgroundData = extractBackgroundDataFromPossiblyTruncatedHistory(session, nodeHistoryId);
  const store = generateStore({
    script,
    session,
    backgroundData,
    answers,
    ...baseMockStore,
  });
  const { session: updatedSession } = await updateSessionStateWithAnswers({
    script,
    session,
    store,
    nodeHistoryId,
    answeringStrategy,
    rawAnswers: newAnswers,
  });

  return {
    ...serializeHistoryAndSchema(script, updatedSession),
    sessionStatus: SessionStatus.Pending,
    auth: {
      visitorId: 'dummy-user',
    },
    ...baseDummySession,
  };
}

// MAIN

export const WizardMemoryPersistenceProvider = React.memo(
  ({ script, children, answers, backgroundData }: PropsWithChildren<WizardMemoryPersistenceProps>): JSX.Element => {
    const { currentStepIndex } = useWizardNavigationContext();
    const [session, setSession] = useState<ChatEngineSession | null>(null);
    const [{ isLoadingInitialServerData, isSubmittingToServer }, setUiState] = useState<{
      isLoadingInitialServerData: boolean;
      isSubmittingToServer: boolean;
    }>({
      isLoadingInitialServerData: true,
      isSubmittingToServer: false,
    });
    const scriptRef = useRef<WizardMemoryPersistenceProps['script'] | undefined>(undefined);

    const { history } = session ?? ({ history: [] } as unknown as SerializedSessionWithHistory);

    // SETUP - INIT
    const initSession = useCallback(() => {
      async function init(): Promise<void> {
        setUiState(val => ({ ...val, isLoadingInitialServerData: true }));
        const { history: fakeSessionHistory, ...sessionRest } = await getInitialFakeSession(
          script,
          answers,
          backgroundData,
        );
        const fetchedSession = {
          ...sessionRest,
          history: fakeSessionHistory.slice(0, 1),
        };
        setSession(fetchedSession);
        setUiState(val => ({ ...val, isLoadingInitialServerData: false }));
      }
      if (scriptRef.current == null || scriptRef.current.uuid !== script.uuid) {
        scriptRef.current = script;
        void init();
      }
    }, [answers, backgroundData, script]);

    useEffect(() => {
      initSession();
    }, [initSession]);

    // ACTIONS - SUBMIT ANSWERS

    const submitAnswersToServer = useCallback(
      async (newAnswers: SchemaData, options?: WizardPersistenceContextSubmissionOptions) => {
        setUiState(val => ({ ...val, isSubmittingToServer: true, serverError: null }));

        const { history: fakeSessionHistory, ...sessionRest } = await progressFakeSession(
          script,
          { history: session!.history, status: session!.sessionStatus },
          newAnswers,
          history[currentStepIndex].id,
          options?.resync ? SessionAnsweringStrategy.Resync : SessionAnsweringStrategy.Standard,
        );
        const modifiedFakeSessionHistory = produce(fakeSessionHistory, draft => {
          draft[currentStepIndex].answers = options?.resync
            ? { ...draft[currentStepIndex].answers, ...newAnswers }
            : newAnswers;
        });
        const fetchedSession: SerializedSessionWithHistory = { ...sessionRest, history: modifiedFakeSessionHistory };

        setSession(fetchedSession);
        setUiState(val => ({ ...val, isSubmittingToServer: false }));
        return { history: fetchedSession.history, success: true };
      },
      [script, session, currentStepIndex, history],
    );

    // SERIALIZE
    const { answersSchema, persistedAnswersByStep, steps, truncateObsoleteLocalSteps } = usePersistenceHelpers({
      history,
      currentStepIndex,
      session,
      setSession,
    });

    const value = useMemo(
      () => ({
        submitAnswers: submitAnswersToServer,
        steps,
        truncateObsoleteLocalSteps,
        persistedAnswersByStep,
        answersSchema,
        isInitializing: isLoadingInitialServerData,
        isSubmitting: isSubmittingToServer,
        isSubmittingLLMRequest: false,
        isErrorFromLLMRequest: false,
        assets: [],
        error: null,
        initSession,
      }),
      [
        answersSchema,
        isSubmittingToServer,
        isLoadingInitialServerData,
        persistedAnswersByStep,
        steps,
        truncateObsoleteLocalSteps,
        submitAnswersToServer,
        initSession,
      ],
    );

    return <WizardPersistenceContext.Provider value={value}>{children}</WizardPersistenceContext.Provider>;
  },
);
